/**
 * Recommended PeerJS signaling lifecycle:
 * - disconnected → peer.reconnect() (keeps the same peer id / media links)
 * - destroyed / close / fatal signaling errors → new Peer() only when idle
 * - never destroy/recreate while a MediaConnection is active
 * - heal on tab visible / browser online
 */

export const PEER_RECONNECT_BASE_MS = 1000;
export const PEER_RECONNECT_MAX_MS = 15000;
export const PEER_ENSURE_TIMEOUT_MS = 10000;

/** Errors that mean local signaling is dead (not "remote peer busy/offline"). */
const FATAL_SIGNALING_ERROR_TYPES = new Set([
  "network",
  "server-error",
  "socket-error",
  "socket-closed",
  "unavailable-id",
]);

export const isPeerConnectionReady = (peer) =>
  Boolean(peer && !peer.destroyed && peer.open && peer.id);

const isMediaCallActive = (mediaCall) =>
  Boolean(mediaCall && mediaCall.open !== false);

/**
 * @param {object} options
 * @param {typeof import("peerjs").default} options.Peer
 * @param {object} options.peerConfig
 * @param {(update: { peer: import("peerjs").default | null, peerId: string, status: "connecting" | "open" | "reconnecting" | "closed" }) => void} options.onStateChange
 * @param {(call: import("peerjs").MediaConnection) => void} options.onIncomingCall
 * @param {() => import("peerjs").MediaConnection | null | undefined} options.getActiveMediaCall
 * @param {(err: unknown) => void} [options.onError]
 * @param {(msg: string, meta?: object) => void} [options.logWarn]
 * @param {(msg: string, meta?: object) => void} [options.logInfo]
 * @param {(msg: string, meta?: object) => void} [options.logError]
 */
export function createPeerLifecycle({
  Peer,
  peerConfig,
  onStateChange,
  onIncomingCall,
  getActiveMediaCall,
  onError,
  logWarn = () => {},
  logInfo = () => {},
  logError = () => {},
  reconnectBaseMs = PEER_RECONNECT_BASE_MS,
  reconnectMaxMs = PEER_RECONNECT_MAX_MS,
  ensureTimeoutMs = PEER_ENSURE_TIMEOUT_MS,
}) {
  let disposed = false;
  let peer = null;
  let reconnectTimer = null;
  let recreateTimer = null;
  let ensurePollTimer = null;
  let attempt = 0;
  let pendingRecreateReason = null;

  const clearTimer = (key) => {
    if (key === "reconnect" && reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (key === "recreate" && recreateTimer) {
      clearTimeout(recreateTimer);
      recreateTimer = null;
    }
    if (key === "ensure" && ensurePollTimer) {
      clearInterval(ensurePollTimer);
      ensurePollTimer = null;
    }
  };

  const clearAllTimers = () => {
    clearTimer("reconnect");
    clearTimer("recreate");
    clearTimer("ensure");
  };

  const backoffMs = () =>
    Math.min(reconnectMaxMs, reconnectBaseMs * 2 ** Math.min(attempt, 4));

  const emit = (status, nextPeer = peer) => {
    onStateChange({
      peer: nextPeer,
      peerId: nextPeer?.id || "",
      status,
    });
  };

  const destroyQuietly = (instance) => {
    if (!instance) return;
    try {
      instance.destroy();
    } catch (e) {
      logWarn("PeerJS destroy failed", e);
    }
  };

  const hasActiveCall = () => isMediaCallActive(getActiveMediaCall?.());

  const tryReconnect = (instance, reason) => {
    if (!instance || instance.destroyed || disposed) return false;
    if (!instance.disconnected && instance.open) return true;
    logWarn("PeerJS reconnect", { reason });
    emit("reconnecting", instance);
    try {
      if (instance.disconnected) {
        instance.reconnect();
      }
      return true;
    } catch (e) {
      logError("PeerJS reconnect failed", e);
      return false;
    }
  };

  const scheduleRecreate = (reason) => {
    if (disposed) return;

    // Mid-call: keep MediaConnection alive; only reconnect signaling.
    if (hasActiveCall()) {
      pendingRecreateReason = reason;
      logWarn("PeerJS recreate deferred (active call)", { reason });
      if (peer && !peer.destroyed) {
        tryReconnect(peer, `defer:${reason}`);
      }
      return;
    }

    if (recreateTimer) return;

    const delay = backoffMs();
    attempt += 1;
    pendingRecreateReason = null;
    logWarn("PeerJS recreate scheduled", { reason, delay });
    emit("connecting", peer);
    recreateTimer = setTimeout(() => {
      recreateTimer = null;
      if (disposed) return;
      if (hasActiveCall()) {
        pendingRecreateReason = reason;
        return;
      }
      destroyQuietly(peer);
      peer = null;
      emit("closed", null);
      createAndBindPeer();
    }, delay);
  };

  /** Call after a media call ends so a deferred recreate can run. */
  const notifyCallEnded = () => {
    if (disposed || !pendingRecreateReason) return;
    const reason = pendingRecreateReason;
    pendingRecreateReason = null;
    scheduleRecreate(`after-call:${reason}`);
  };

  const createAndBindPeer = () => {
    if (disposed) return;
    const instance = new Peer(peerConfig);
    peer = instance;
    emit("connecting", instance);

    instance.on("open", (id) => {
      if (disposed || peer !== instance) return;
      attempt = 0;
      clearAllTimers();
      pendingRecreateReason = null;
      logInfo("PeerJS open", { id });
      onStateChange({ peer: instance, peerId: id, status: "open" });
    });

    instance.on("call", (call) => {
      if (disposed || peer !== instance) return;
      onIncomingCall(call);
    });

    instance.on("error", (err) => {
      if (disposed || peer !== instance) return;
      onError?.(err);
      logError("PeerJS error", err);
      const type = err?.type;
      // peer-unavailable = remote id missing — do not recreate local peer
      if (instance.destroyed || FATAL_SIGNALING_ERROR_TYPES.has(type)) {
        scheduleRecreate(`error:${type || "unknown"}`);
      }
    });

    instance.on("disconnected", () => {
      if (disposed || peer !== instance || instance.destroyed) return;
      logWarn("PeerJS disconnected from signaling server");
      const ok = tryReconnect(instance, "disconnected-event");
      if (!ok) {
        scheduleRecreate("reconnect-throw");
        return;
      }

      clearTimer("reconnect");
      const delay = backoffMs();
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (disposed || peer !== instance) return;
        if (instance.open) return;
        if (instance.destroyed) {
          scheduleRecreate("destroyed-after-reconnect");
          return;
        }
        // Still disconnected: one more reconnect, then recreate if idle
        if (instance.disconnected) {
          const again = tryReconnect(instance, "reconnect-retry");
          if (!again) {
            scheduleRecreate("reconnect-retry-failed");
            return;
          }
        }
        clearTimer("reconnect");
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          if (disposed || peer !== instance) return;
          if (!instance.open) {
            scheduleRecreate("reconnect-timeout");
          }
        }, backoffMs());
      }, delay);
    });

    instance.on("close", () => {
      if (disposed || peer !== instance) return;
      logWarn("PeerJS closed");
      peer = null;
      emit("closed", null);
      scheduleRecreate("close");
    });
  };

  const ensureHealthy = () => {
    if (disposed) return;
    if (!peer || peer.destroyed) {
      scheduleRecreate("ensure-missing");
      return;
    }
    if (peer.open) return;
    if (!tryReconnect(peer, "ensure-healthy")) {
      scheduleRecreate("ensure-reconnect-failed");
      return;
    }
    clearTimer("reconnect");
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (disposed) return;
      if (!peer || peer.destroyed || !peer.open) {
        scheduleRecreate("ensure-stuck");
      }
    }, backoffMs());
  };

  const ensureReady = () =>
    new Promise((resolve, reject) => {
      if (disposed) {
        reject(new Error("peer-disposed"));
        return;
      }
      if (isPeerConnectionReady(peer)) {
        resolve(peer.id);
        return;
      }
      ensureHealthy();
      const started = Date.now();
      clearTimer("ensure");
      ensurePollTimer = setInterval(() => {
        if (disposed) {
          clearTimer("ensure");
          reject(new Error("peer-disposed"));
          return;
        }
        if (isPeerConnectionReady(peer)) {
          clearTimer("ensure");
          resolve(peer.id);
          return;
        }
        if (Date.now() - started >= ensureTimeoutMs) {
          clearTimer("ensure");
          reject(new Error("peer-ensure-timeout"));
        }
      }, 200);
    });

  const onVisibility = () => {
    if (document.visibilityState === "visible") ensureHealthy();
  };
  const onOnline = () => ensureHealthy();

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);

  createAndBindPeer();

  return {
    ensureReady,
    ensureHealthy,
    notifyCallEnded,
    getPeer: () => peer,
    destroy() {
      disposed = true;
      clearAllTimers();
      pendingRecreateReason = null;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      destroyQuietly(peer);
      peer = null;
      emit("closed", null);
    },
  };
}
